/**
 * LoginBrandingPanel — the branded half of the login page.
 *
 * This is the ONE implementation. The login page renders it, and so does the
 * White-Label editor's live preview, which is the point: the preview used to
 * be a hand-built lookalike (its own class names, its own tick icon, no
 * animated frame), so "what you see" and "what your users get" could drift
 * apart silently — and did. Whatever this component renders IS the login
 * page, so the preview cannot lie.
 *
 * Purely presentational: everything it draws comes from the `brand` it is
 * handed, which lets the preview pass the UNSAVED merged brand while the real
 * page passes the resolved one.
 */

import type { CSSProperties } from 'react';
import { SOLID_AGENT_COLORS } from '@/brand';
import type { BrandConfig } from '@/brand/types';
import { safeBrandUrl } from '@/brand/safe-url';
import { useLanguage } from '@/hooks/useLanguage';
import '@modules/auth/pages/LoginPage.css';

/** Semantic feature-tick colours resolve through the theme, not literals. */
const SEMANTIC_FEATURE_COLORS: Record<string, string> = {
  primary: 'var(--primary)',
  secondary: 'var(--secondary)',
  success: 'var(--success)',
  warning: 'var(--warning)',
  error: 'var(--error)',
  info: 'var(--info)',
  accent: 'var(--accent)',
};

export function resolveFeatureColor(c: string | undefined): string {
  if (!c) return 'var(--primary)';
  return SEMANTIC_FEATURE_COLORS[c] ?? c;
}

export function FeatureTickIcon({ color }: { color: string }) {
  // Visual: solid colored disc + thin dark inner ring + bold dark check.
  // Matches the M² brand-book check icons (and reads cleanly against any
  // sufficiently saturated tenant color). The dark ink is intentionally not
  // a brand variable — it's the contrast layer, the way black ink works on a
  // colored sticker, and applies the same way to every brand.
  return (
    <span className="feature-icon" aria-hidden="true">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="12" fill={color} />
        <circle cx="12" cy="12" r="8.5" fill="none" stroke="#000" strokeWidth="1" />
        <path d="M8 12.5l3 3 5.5-6" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(n => Number.isNaN(n))) return hex;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Build the CSS custom properties the looping branding-frame animation reads
 * (see LoginPage.css `.branding-frame-*`). The four color stops come from the
 * tenant's agent palette so the spinner follows the brand; falls back to the
 * historical M² frame colors when the palette is missing or short. CSS
 * `@keyframes` can't read a JS array, so we bridge it through these vars.
 */
export function buildFrameVars(palette: string[] | undefined): CSSProperties {
  // Brand-neutral fallback sourced from the brand layer (not hardcoded hex)
  // for tenants whose agentColors palette has fewer than 4 entries.
  const FALLBACK = [
    SOLID_AGENT_COLORS.blue,
    SOLID_AGENT_COLORS.green,
    SOLID_AGENT_COLORS.red,
    SOLID_AGENT_COLORS.yellow,
  ];
  const stops = [0, 1, 2, 3].map(i => palette?.[i] ?? FALLBACK[i]);
  const vars: Record<string, string> = {};
  stops.forEach((c, i) => {
    const n = i + 1;
    vars[`--frame-color-${n}`] = c;
    vars[`--frame-glow-${n}`] = hexToRgba(c, 0.85);
    vars[`--frame-base-${n}`] = hexToRgba(c, 0.5);
  });
  return vars as CSSProperties;
}

interface Props {
  brand: BrandConfig;
  /** The theme-correct logo (light/dark). The real page resolves it through
   *  useBrandAssets; the preview passes the merged brand's own asset. */
  logo: string;
  /** The preview renders inside a scaled box where an outbound link would be
   *  a trap, so it opts out of wrapping the logo in an anchor. */
  linkLogo?: boolean;
}

export function LoginBrandingPanel({ brand, logo, linkLogo = true }: Props) {
  const { t } = useLanguage();

  const logoImg = (
    <img
      src={logo}
      alt={brand.name}
      className="branding-logo"
      style={linkLogo ? { cursor: 'pointer' } : undefined}
    />
  );

  return (
    /* `frameVars` exposes the brand's agent palette to the looping frame
       animation as CSS custom properties (--frame-color/glow/base-N). */
    <div className="login-branding" style={buildFrameVars(brand.agentColors)}>
      {/* Continuously traveling stroke around the panel — same language
          as the in-app agent-selection animation, on auto-loop. */}
      <svg className="branding-frame" aria-hidden="true">
        {/* Static base — keeps the full frame visible at all times. */}
        <rect
          className="branding-frame-base"
          x="18"
          y="18"
          width="calc(100% - 36px)"
          height="calc(100% - 36px)"
          rx="22"
          ry="22"
          fill="none"
        />
        {/* Glowing highlight — travels around the perimeter on loop. */}
        <rect
          className="branding-frame-path"
          x="18"
          y="18"
          width="calc(100% - 36px)"
          height="calc(100% - 36px)"
          rx="22"
          ry="22"
          pathLength="100"
          fill="none"
        />
      </svg>
      <div className="branding-content">
        {/* An unsafe companyUrl renders as a plain logo, not a link: this
            href is clickable BEFORE login, so a javascript:/data: value
            would run for any visitor of the tenant. */}
        {linkLogo && safeBrandUrl(brand.companyUrl)
          ? (
            <a
              href={safeBrandUrl(brand.companyUrl)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {logoImg}
            </a>
          )
          : logoImg}
        <p className="branding-eyebrow" dir="auto">{brand.loginEyebrow ?? brand.name}</p>
        <h2 className="branding-tagline" dir="auto">{t(brand.tagline, { defaultValue: brand.tagline })}</h2>

        <div className="branding-features">
          {(brand.loginFeatures || []).map((f, i) => (
            <div className="feature-item" key={`${i}:${f.label}`}>
              <FeatureTickIcon color={resolveFeatureColor(f.color)} />
              <span dir="auto">{t(f.label, { defaultValue: f.label })}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default LoginBrandingPanel;
